import i18next from "i18next"
import { Navigate } from "react-router-dom"
import { lazy } from "react"

import discussionForumRoute from "@/pages/DiscussionForum/route"
import myOrganisationRoute from "@/pages/MyOrganisation/route"
import allOrganisationsRoute from "@/pages/AllOrganisations/route"
import universityRoute from "@/pages/Universities/route"
import allUsersRoute from "@/pages/AllUsers/route"
import allInternalUsersRoute from "@/pages/InternalUsers/route"
import profileRoute from "@/pages/Profile/route"
import fileManagementRoute from "@/pages/FileManagement/route"
import gamesRoute from "@/pages/Games/route"
import unityTest from "@/pages/UnityTest/route"
import knowledgeBaseRoute from "@/pages/KnowledgeBase/route"
import analytics from "@/pages/Analytics/route"
import notificationRoute from "@/pages/AppNotifications/route"
import facebookAdAccountRoute from "@/pages/FacebookAdAccount/route"
import homeRoute from "@/pages/Home/route"
import AuthenticatedRoute from "@/components/AuthenticatedRoute"

const PageTitleProvider = lazy(() => import("@/providers/PageTitleProvider"))
const GoogleAnalyticsProvider = lazy(
  () => import("@/providers/GoogleAnalyticsProvider"),
)
const SomethingWentWrong = lazy(() => import("@/components/SomethingWentWrong"))
const AuthorisationRoute = lazy(() => import("@/components/AuthorisedRoute"))

const OnboardingLayout = lazy(
  () => import("@/components/Layout/Onboarding/OnboardingLayout"),
)

const Layout = lazy(() => import("@/components/Layout/Layout"))

const Login = lazy(() => import("@/pages/Login/Login"))
const AdminLogin = lazy(() => import("@/pages/Login/AdminLogin"))

const Register = lazy(() => import("@/pages/Register/Register"))
const RegisterSuccess = lazy(() => import("@/pages/Register/RegisterSuccess"))

const Auth = lazy(() => import("@/pages/Auth/Auth"))

const VerifyEmail = lazy(() => import("@/pages/VerifyEmail/VerifyEmail"))
const ForgotPassword = lazy(
  () => import("@/pages/ForgotPassword/ForgotPassword"),
)

const OrganisationSetup = lazy(
  () => import("@/pages/OrganisationSetup/OrganisationSetup"),
)

const HybridSubmission = lazy(
  () => import("@/pages/Games/hybrid-submission/HybridSubmission"),
)

const PageNotFound = lazy(() => import("@/components/NotFound/PageNotFound"))

const hitseekerRoutes = [
  {
    element: (
      <PageTitleProvider>
        <GoogleAnalyticsProvider>
          <OnboardingLayout />
        </GoogleAnalyticsProvider>
      </PageTitleProvider>
    ),
    errorElement: <SomethingWentWrong />,
    children: [
      {
        path: "/login",
        handle: {
          pageTitle: () =>
            `${i18next.t("pageTitle.loginPage", {
              ns: "onboarding",
            })} - ${i18next.t("label.hitseeker", { ns: "common" })}`,
        },
        element: <Login />,
      },
      {
        path: "/register",
        handle: {
          pageTitle: () =>
            `${i18next.t("pageTitle.registerPage", {
              ns: "onboarding",
            })} - ${i18next.t("label.hitseeker", { ns: "common" })}`,
        },
        element: <Register />,
      },
      { path: "/register-success", element: <RegisterSuccess /> },
      {
        path: "/forgot-password",
        handle: {
          pageTitle: () =>
            `${i18next.t("pageTitle.forgotPasswordPage", {
              ns: "onboarding",
            })} - ${i18next.t("label.hitseeker", { ns: "common" })}`,
        },
        element: <ForgotPassword />,
      },
      { path: "/set-password", element: <Register onlySetPassword /> },
      { path: "/auth", element: <Auth scope="external" /> },
      { path: "/admin/login", element: <AdminLogin /> },
      { path: "/admin/auth", element: <Auth scope="internal" /> },
      { path: "/verify", element: <VerifyEmail /> },
    ],
  },
  {
    path: "/organisation-setup",
    handle: { pageTitle: () => "Organisation Setup - Hitseeker" },
    errorElement: <SomethingWentWrong />,
    element: (
      <AuthenticatedRoute>
        <PageTitleProvider>
          <GoogleAnalyticsProvider>
            <OrganisationSetup />
          </GoogleAnalyticsProvider>
        </PageTitleProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: "/hybrid",
    element: <HybridSubmission />,
  },
  {
    path: "/",
    element: (
      <AuthenticatedRoute>
        <AuthorisationRoute>
          <PageTitleProvider>
            <GoogleAnalyticsProvider>
              <Layout />
            </GoogleAnalyticsProvider>
          </PageTitleProvider>
        </AuthorisationRoute>
      </AuthenticatedRoute>
    ),
    errorElement: <SomethingWentWrong />,
    handle: {
      pageTitle: () =>
        `${i18next.t("label.home")} - ${i18next.t("label.hitseeker")}`,
      crumb: () => i18next.t("label.home"),
    },
    children: [
      homeRoute,
      analytics,
      knowledgeBaseRoute,
      fileManagementRoute,
      discussionForumRoute,
      myOrganisationRoute,
      allOrganisationsRoute,
      profileRoute,
      gamesRoute,
      unityTest,
      notificationRoute,
      facebookAdAccountRoute,
      allUsersRoute,
      allInternalUsersRoute,
      universityRoute,
    ],
  },
  /* Redirection URLS for old portal routes */
  {
    path: "/signup",
    element: <Navigate to="/register" replace={true} />,
  },
  {
    path: "/submit-game/info",
    element: <Navigate to="/register" replace={true} />,
  },
  {
    path: "/coaching",
    element: <Navigate to="/register" replace={true} />,
  },
  {
    path: "/wallet",
    element: <Navigate to="/register" replace={true} />,
  },
  {
    path: "/account",
    element: <Navigate to="/register" replace={true} />,
  },
  /* Redirection URLS end */
  {
    path: "*",
    element: <PageNotFound />,
  },
]

export default hitseekerRoutes
